import * as Sentry from '@sentry/react';
import { useEffect, useState, type JSX } from 'react';
import { Navigate } from 'react-router-dom';

import { isTransientAuthError } from '~/utils/auth';
import { supabase } from '~/utils/supabaseClient';

export default function RequireMFA({ children }: { children: JSX.Element }) {
  const [readyToShow, setReadyToShow] = useState(false);
  const [showMFAScreen, setShowMFAScreen] = useState(false);
  const [requiredMFASetup, setRequiredMFASetup] = useState(false);

  useEffect(() => {
    // Safe to float only because the IIFE catches everything below.
    void (async () => {
      try {
        const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        if (error) {
          throw error;
        }

        const { data: userDate, error: userError } = await supabase.auth.getUser();
        if (userError) {
          throw userError;
        }

        const { data: factorsData, error: factorsError } = await supabase.auth.mfa.listFactors();
        if (factorsError) {
          throw factorsError;
        }

        if (userDate.user.app_metadata?.mfa_required === true && factorsData.totp.length === 0) {
          setRequiredMFASetup(true);
        }
        if (data.nextLevel === 'aal2' && data.nextLevel !== data.currentLevel) {
          setShowMFAScreen(true);
        }
      } catch (error) {
        // All three calls above reach the network, and supabase-auth-js returns
        // a transport failure inside the `{ data, error }` envelope rather than
        // rejecting — so the `throw`s turn a WiFi blip into a rejection. With no
        // `catch` it escaped this effect's floating promise and reached
        // `onunhandledrejection` on every authenticated page (ARTIC-HUB-100).
        //
        // The preflight is advisory: it only picks /mfa-setup or /mfa-challenge.
        // It must not sign out or redirect to /login, even on a 401 — LoginPage's
        // mount effect clears the auth cookie on the shared parent domain, which
        // would sign the user out of every tab and every microsite, and with a
        // live session it loops instead (ARTIC-HUB-12X). A dead session belongs
        // to App.tsx's onAuthStateChange and the api-client refresh path.
        //
        // So report it and let the user through, keeping a blip distinct from a
        // genuine rejection.
        const transient = isTransientAuthError(error);
        Sentry.captureException(error, {
          level: transient ? 'warning' : 'error',
          tags: {
            feature: 'mfa-guard',
            auth_failure: transient ? 'transient' : 'rejected',
          },
        });
      } finally {
        setReadyToShow(true);
      }
    })();
  }, []);

  if (readyToShow) {
    if (requiredMFASetup) {
      // Redirect to MFA setup screen if MFA is required but not setup
      return <Navigate to="/mfa-setup" replace />;
    }
    if (showMFAScreen) {
      // Redirect to MFA screen if MFA is required
      return <Navigate to="/mfa-challenge" replace />;
    }

    return children;
  }

  return <></>;
}
